import React, { useEffect, useState } from "react";
import api, { formatApiError, asArray } from "../../lib/api";
import { Plus, Trash2, Edit3, X, GripVertical } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border-b border-black/15 focus:border-[#0060A7] outline-none py-2";

const blank = () => ({
    value: "",
    label_ar: "",
    label_en: "",
    icon: "Sparkles",
    order: 0,
    category: "",
    published: true,
});

const ICON_SUGGESTIONS = [
    "Network", "Users", "Megaphone", "FileText", "Languages",
    "Briefcase", "Rocket", "Globe", "Newspaper", "Tv", "Award", "Building2",
];

export default function AdminStats() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try { setItems(asArray((await api.get("/admin/stats")).data, "stats")); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = { ...editing };
            payload.order = Number(payload.order) || 0;
            if (payload.id) await api.patch(`/admin/stats/${payload.id}`, payload);
            else await api.post("/admin/stats", payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this stat?")) return;
        try { await api.delete(`/admin/stats/${id}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    if (editing) {
        const e = editing;
        return (
            <div data-testid="admin-stat-form">
                <button onClick={() => setEditing(null)} className="text-sm text-[#6B7280] mb-6 inline-flex items-center gap-2">
                    <X size={14} /> Cancel
                </button>
                <h2 className="heading text-2xl tracking-tight mb-8 font-bold">
                    {e.id ? "Edit Stat" : "New Stat"}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8">
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Value (e.g. 300, 2,000, 100,000)</label>
                        <input data-testid="stat-value" className={inputCls} value={e.value} onChange={(ev) => setEditing({ ...e, value: ev.target.value })} />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Order</label>
                        <input type="number" className={inputCls} value={e.order} onChange={(ev) => setEditing({ ...e, order: ev.target.value })} />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Label (AR)</label>
                        <input data-testid="stat-label-ar" className={inputCls} value={e.label_ar} onChange={(ev) => setEditing({ ...e, label_ar: ev.target.value })} />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Label (EN)</label>
                        <input data-testid="stat-label-en" className={inputCls} value={e.label_en} onChange={(ev) => setEditing({ ...e, label_en: ev.target.value })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Icon (Lucide name)</label>
                        <input data-testid="stat-icon" className={inputCls} value={e.icon} onChange={(ev) => setEditing({ ...e, icon: ev.target.value })} />
                        <div className="mt-2 flex flex-wrap gap-2">
                            {ICON_SUGGESTIONS.map((name) => (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => setEditing({ ...e, icon: name })}
                                    className={`text-xs px-2 py-1 border ${e.icon === name ? "bg-[#002951] text-white border-[#002951]" : "border-black/15 hover:border-[#0060A7]"}`}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Category (leave blank for Home, or "influencer" for Influencers page)</label>
                        <select
                            data-testid="stat-category"
                            className={inputCls}
                            value={e.category || ""}
                            onChange={(ev) => setEditing({ ...e, category: ev.target.value })}
                        >
                            <option value="">Home / About (default)</option>
                            <option value="influencer">Influencers page</option>
                        </select>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm md:col-span-2">
                        <input type="checkbox" checked={e.published} onChange={(ev) => setEditing({ ...e, published: ev.target.checked })} />
                        Show on site
                    </label>
                </div>
                <div className="mt-6">
                    <button onClick={save} data-testid="stat-save" className="bg-[#002951] hover:bg-[#0060A7] text-white px-8 py-3 text-sm uppercase tracking-[0.2em]">Save</button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-stats-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="heading text-2xl tracking-tight font-bold">Network Stats</h1>
                    <p className="text-sm text-[#6B7280] mt-1">إدارة أرقام الشبكة — تظهر في الصفحة الرئيسية وصفحة "عن ليجيندز" مع تأثير عدّاد متحرك.</p>
                </div>
                <button onClick={() => setEditing(blank())} data-testid="add-stat-btn" className="bg-[#002951] hover:bg-[#0060A7] text-white px-5 py-2.5 text-sm inline-flex items-center gap-2">
                    <Plus size={14} /> New Stat
                </button>
            </div>
            <div className="bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-[#EFF0F1] text-left text-xs uppercase tracking-[0.2em] text-[#6B7280]">
                        <tr>
                            <th className="p-4 w-8"></th>
                            <th className="p-4">Value</th>
                            <th className="p-4">Label</th>
                            <th className="p-4">Icon</th>
                            <th className="p-4">Order</th>
                            <th className="p-4">Status</th>
                            <th className="p-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((s) => (
                            <tr key={s.id} className="border-t border-black/5" data-testid={`stat-row-${s.id}`}>
                                <td className="p-4 text-[#6B7280]"><GripVertical size={14} /></td>
                                <td className="p-4 heading text-lg font-bold">{s.value}</td>
                                <td className="p-4">
                                    <div>{s.label_en}</div>
                                    <div className="text-xs text-[#6B7280]">{s.label_ar}</div>
                                </td>
                                <td className="p-4 text-[#6B7280]">{s.icon}</td>
                                <td className="p-4 text-[#6B7280]">{s.order}</td>
                                <td className="p-4">{s.published ? <span className="text-emerald-600 text-xs">Visible</span> : <span className="text-[#6B7280] text-xs">Hidden</span>}</td>
                                <td className="p-4 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(s)} className="mr-3 text-[#0060A7]" data-testid={`edit-stat-${s.id}`}><Edit3 size={16} /></button>
                                    <button onClick={() => remove(s.id)} className="text-red-500" data-testid={`delete-stat-${s.id}`}><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="p-8 text-center text-[#6B7280]">No stats yet. Click "New Stat" to add one.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
